import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  App,
  Button,
  Table,
  Space,
  Tag,
  Input,
  Modal,
  Select,
  Row,
  Col,
  Typography,
  Statistic,
  Progress,
  Tabs,
  Alert,
  Form,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  PlusOutlined,
  ProjectOutlined,
  EditOutlined,
  EyeOutlined,
  DollarOutlined,
  ClockCircleOutlined,
  TeamOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  SafetyOutlined,
  AuditOutlined,
  LineChartOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useProjects, projectKeys } from '../../hooks/useProjects';
import { projectService } from '../../services/project.service';
import { userService } from '../../services/user.service';
import { NobleCard, NobleButton, HealthScore, CompletionRing, UrgentTaskCard, CriticalAlertCard } from '@/components/noble';
import { AedDirhamSign } from '@/components/currency';
import { useCurrency } from '@/contexts/CurrencyContext';
import { useAuthStore } from '@/store/authStore';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';

const { Title, Text } = Typography;
const { Search } = Input;
interface ProjectCost {
  material: number;
  labor: number;
  subcontract: number;
  equipment: number;
  overhead: number;
}

interface Project {
  id: string;
  key: string;
  projectCode: string;
  projectName: string;
  customer: string;
  projectManager: string;
  status: 'Planning' | 'In Progress' | 'On Hold' | 'Completed' | 'Cancelled';
  health: 'Healthy' | 'At Risk' | 'Critical';
  startDate: string;
  endDate: string;
  budgetAmount: number;
  /** Estimation budget (BOQ + TRE) or project master fallback */
  budgetedTotal: number;
  forecastProfit: number;
  forecastMarginPct: number;
  billedProfit: number;
  billedMarginPct: number;
  invoicedTotal: number;
  customer_id?: string;
  billedAmount: number;
  progressPercent: number;
  costBreakdown: ProjectCost;
  teamSize: number;
  hoursLogged: number;
  description?: string;
}

const statusColors: { [key: string]: string } = {
  'Planning': 'default',
  'In Progress': 'blue',
  'On Hold': 'orange',
  'Completed': 'green',
  'Cancelled': 'red',
};

const healthColors: { [key: string]: string } = {
  'Healthy': '#52c41a',
  'At Risk': '#faad14',
  'Critical': '#f5222d',
};

const healthIcons: { [key: string]: React.ReactNode } = {
  'Healthy': <CheckCircleOutlined />,
  'At Risk': <WarningOutlined />,
  'Critical': <WarningOutlined />,
};

export const ProjectsPage: React.FC = () => {
  const navigate = useNavigate();
  const { message } = App.useApp();
  const { formatCurrency } = useCurrency();
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [healthFilter, setHealthFilter] = useState<string>('all-health');
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameTarget, setRenameTarget] = useState<Project | null>(null);
  const [renameSubmitting, setRenameSubmitting] = useState(false);
  const [renameForm] = Form.useForm<{ project_name: string }>();

  const canEditProject = Boolean(
    user?.is_super_admin || user?.permissions?.includes('project.edit')
  );
  const [searchText, setSearchText] = useState('');
  const debouncedSearch = useDebouncedValue(searchText.trim(), 300);
  const { data: projectsData = [], isLoading, error } = useProjects({
    ...(statusFilter !== 'all' ? { project_status: statusFilter } : {}),
    ...(debouncedSearch ? { search: debouncedSearch } : {}),
  });

  const { data: users = [] } = useQuery({
    queryKey: ['users', 'active', 'projects-form'],
    queryFn: () => userService.getAll({ is_active: true }),
    staleTime: 5 * 60 * 1000,
  });

  const userNameById = React.useMemo(() => {
    const m = new Map<string, string>();
    for (const u of users) {
      const label = [u.first_name, u.last_name].filter(Boolean).join(' ').trim() || u.email || u.username;
      m.set(u.id, label);
    }
    return m;
  }, [users]);

  // Transform API data to match the Project interface
  const projects: Project[] = React.useMemo(() => {
    if (!Array.isArray(projectsData) || projectsData.length === 0) {
      return [];
    }

    const out: Project[] = [];
    for (const p of projectsData as any[]) {
      if (!p) continue;

      const lf = p.list_financials;
      const budgetedTotal = Number(
        lf?.estimation_budget ?? p.total_budgeted_cost ?? p.budgetedTotal ?? 0
      );
      const contractVal = Number(
        lf?.ordered_total ?? p.contract_value ?? p.budgetAmount ?? 0
      );
      const forecastProfit = Number(lf?.forecast_profit ?? contractVal - budgetedTotal);
      const forecastMarginPct = Number(
        lf?.forecast_margin_pct ??
          (contractVal > 0 ? (forecastProfit / contractVal) * 100 : 0)
      );
      const billedProfit = Number(lf?.billed_profit ?? 0);
      const billedMarginPct = Number(lf?.billed_margin_pct ?? 0);
      const invoicedTotal = Number(lf?.invoiced_total ?? 0);
      const pct = Number(p.completion_percentage ?? 0);
      let health: 'Healthy' | 'At Risk' | 'Critical' = 'Healthy';
      if (lf?.any_bva_over || (contractVal > 0 && billedMarginPct < 0 && invoicedTotal > 0)) {
        health = 'Critical';
      } else if (contractVal > 0 && budgetedTotal > contractVal) {
        health = 'Critical';
      } else if (forecastMarginPct < 0 && contractVal > 0) {
        health = 'At Risk';
      } else if (pct < 50 && pct > 0 && (p.project_status === 'In Progress' || p.project_status === 'Planning')) {
        health = 'At Risk';
      } else if (pct === 0 && p.project_status === 'In Progress') {
        health = 'At Risk';
      }

      out.push({
        id: p.id,
        key: p.id,
        projectCode: p.project_number || p.projectCode || '',
        projectName: p.project_name || p.projectName || '',
        customer: p.customer_name || p.customer || '',
        customer_id: p.customer_id,
        projectManager: p.project_manager || 'N/A',
        status: (p.project_status || p.status || 'Planning') as 'Planning' | 'In Progress' | 'On Hold' | 'Completed' | 'Cancelled',
        health,
        startDate: p.start_date || p.startDate || '',
        endDate: p.planned_end_date || p.endDate || '',
        budgetAmount: contractVal,
        budgetedTotal,
        forecastProfit,
        forecastMarginPct,
        billedProfit,
        billedMarginPct,
        invoicedTotal,
        billedAmount: invoicedTotal,
        progressPercent: pct,
        costBreakdown: {
          material: Number(p.budgeted_material_cost ?? 0),
          labor: Number(p.budgeted_labor_cost ?? 0),
          subcontract: Number(p.budgeted_subcontract_cost ?? 0),
          equipment: Number(p.budgeted_equipment_cost ?? 0),
          overhead: Number(p.budgeted_overhead_cost ?? 0),
        },
        teamSize: 0,
        hoursLogged: 0,
        description: p.description || '',
      });
    }
    return out;
  }, [projectsData]);

  // Client filter for health only (search is server-side)
  const filteredProjects = React.useMemo(() => {
    return projects.filter((project) => {
      if (healthFilter !== 'all-health' && project.health !== healthFilter) {
        return false;
      }
      return true;
    });
  }, [projects, healthFilter]);

  const columns: ColumnsType<Project> = [
    {
      title: 'Project Code',
      dataIndex: 'projectCode',
      key: 'projectCode',
      width: 130,
      sorter: (a, b) => a.projectCode.localeCompare(b.projectCode),
    },
    {
      title: 'Project Details',
      key: 'details',
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{record.projectName}</Text>
          <Text type="secondary" style={{ fontSize: '11px' }}>
            Customer: {record.customer}
          </Text>
          <Text type="secondary" style={{ fontSize: '11px' }}>
            PM:{' '}
            {userNameById.get(record.projectManager) ||
              (record.projectManager === 'N/A' ? '—' : record.projectManager)}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Status & Health',
      key: 'statusHealth',
      width: 140,
      render: (_, record) => (
        <Space direction="vertical" size={4}>
          <Tag color={statusColors[record.status]}>{record.status}</Tag>
          <Tag 
            color={healthColors[record.health]} 
            icon={healthIcons[record.health]}
            style={{ fontSize: '11px' }}
          >
            {record.health}
          </Tag>
        </Space>
      ),
      filters: [
        { text: 'Planning', value: 'Planning' },
        { text: 'In Progress', value: 'In Progress' },
        { text: 'On Hold', value: 'On Hold' },
        { text: 'Completed', value: 'Completed' },
      ],
      onFilter: (value, record) => record.status === value,
    },
    {
      title: 'Progress',
      key: 'progress',
      width: 120,
      render: (_, record) => (
        <Progress
          percent={record.progressPercent}
          size="small"
          status={record.progressPercent === 100 ? 'success' : 'active'}
        />
      ),
      sorter: (a, b) => a.progressPercent - b.progressPercent,
    },
    {
      title: 'Contract',
      dataIndex: 'budgetAmount',
      key: 'budgetAmount',
      width: 120,
      align: 'right',
      render: (amount) => (
        <Text strong>{amount > 0 ? formatCurrency(amount) : '—'}</Text>
      ),
      sorter: (a, b) => a.budgetAmount - b.budgetAmount,
    },
    {
      title: 'Est. budget',
      key: 'budgetedTotal',
      width: 120,
      align: 'right',
      render: (_, record) => {
        const pctOfContract =
          record.budgetAmount > 0 ? (record.budgetedTotal / record.budgetAmount) * 100 : 0;
        const isOverContract = record.budgetAmount > 0 && record.budgetedTotal > record.budgetAmount;

        return (
          <Space direction="vertical" size={0}>
            <Text strong style={{ color: isOverContract ? '#f5222d' : undefined }}>
              {record.budgetedTotal > 0 ? formatCurrency(record.budgetedTotal) : '—'}
            </Text>
            {record.budgetAmount > 0 ? (
              <Text type="secondary" style={{ fontSize: '10px' }}>
                {pctOfContract.toFixed(1)}% of contract
              </Text>
            ) : null}
          </Space>
        );
      },
      sorter: (a, b) => a.budgetedTotal - b.budgetedTotal,
    },
    {
      title: 'Forecast P/L',
      key: 'margin',
      width: 120,
      align: 'right',
      render: (_, record) => {
        const hasContract = record.budgetAmount > 0;
        const margin = record.forecastProfit;
        const marginPercent = record.forecastMarginPct;

        return (
          <Space direction="vertical" size={0} style={{ textAlign: 'right' }}>
            <Text
              strong
              style={{
                color: !hasContract ? undefined : margin >= 0 ? '#52c41a' : '#f5222d',
              }}
            >
              {hasContract ? formatCurrency(margin) : '—'}
            </Text>
            {hasContract ? (
              <Text type="secondary" style={{ fontSize: '10px' }}>
                {marginPercent.toFixed(1)}% margin
              </Text>
            ) : null}
            {record.invoicedTotal > 0 ? (
              <Text type="secondary" style={{ fontSize: '10px' }}>
                Billed:{' '}
                <span style={{ color: record.billedProfit >= 0 ? '#52c41a' : '#f5222d' }}>
                  {formatCurrency(record.billedProfit)}
                </span>
              </Text>
            ) : null}
          </Space>
        );
      },
    },
    {
      title: 'Timeline',
      key: 'timeline',
      width: 160,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text style={{ fontSize: '11px' }}>
            Start:{' '}
            {record.startDate && dayjs(record.startDate).isValid()
              ? dayjs(record.startDate).format('DD-MMM-YYYY')
              : '—'}
          </Text>
          <Text style={{ fontSize: '11px' }}>
            End:{' '}
            {record.endDate && dayjs(record.endDate).isValid()
              ? dayjs(record.endDate).format('DD-MMM-YYYY')
              : '—'}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: canEditProject ? 300 : 180,
      render: (_, record) => (
        <Space wrap size="small">
          <Button
            type="text"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleView(record)}
          >
            View
          </Button>
          <Button
            type="text"
            size="small"
            icon={<LineChartOutlined />}
            onClick={() => navigate(`/prj/projects/${record.id}/status-check`)}
          >
            Health
          </Button>
          {canEditProject ? (
            <>
              <Button
                type="text"
                size="small"
                icon={<EditOutlined />}
                onClick={() => openRenameModal(record)}
              >
                Rename
              </Button>
              <Button type="text" size="small" onClick={() => handleEdit(record)}>
                Edit
              </Button>
            </>
          ) : null}
        </Space>
      ),
    },
  ];

  const openRenameModal = (project: Project) => {
    setRenameTarget(project);
    renameForm.setFieldsValue({ project_name: project.projectName });
    setRenameOpen(true);
  };

  const submitRename = async () => {
    if (!renameTarget) return;
    try {
      const values = await renameForm.validateFields();
      const name = String(values.project_name || '').trim();
      if (!name) {
        message.warning('Project name is required');
        return;
      }
      setRenameSubmitting(true);
      await projectService.updateProject(renameTarget.id, {
        project_name: name,
        customer_id: renameTarget.customer_id,
        customer_name: renameTarget.customer,
      });
      message.success('Project name updated');
      setRenameOpen(false);
      setRenameTarget(null);
      await queryClient.invalidateQueries({ queryKey: projectKeys.all });
    } catch (e: unknown) {
      if ((e as { errorFields?: unknown })?.errorFields) return;
      message.error((e as Error)?.message || 'Could not update project name');
    } finally {
      setRenameSubmitting(false);
    }
  };

  const handleView = (project: Project) => {
    const costTotal = Object.values(project.costBreakdown).reduce((sum, cost) => sum + cost, 0);
    const fmtDate = (d: string) =>
      d && dayjs(d).isValid() ? dayjs(d).format('DD-MMM-YYYY') : '—';
    
    Modal.info({
      title: `Project Details: ${project.projectName}`,
      width: 900,
      footer: (
        <Space>
          <Button onClick={() => Modal.destroyAll()}>Close</Button>
          <Button
            onClick={() => {
              Modal.destroyAll();
              navigate(`/scm/inventory/material-requests/new?projectId=${project.id}`);
            }}
          >
            New material request
          </Button>
          <Button
            onClick={() => {
              Modal.destroyAll();
              navigate(`/scm/inventory/site-materials?projectId=${project.id}`);
            }}
          >
            Site materials
          </Button>
          {canEditProject ? (
            <>
              <Button
                onClick={() => {
                  Modal.destroyAll();
                  openRenameModal(project);
                }}
              >
                Rename
              </Button>
              <Button
                type="primary"
                onClick={() => {
                  Modal.destroyAll();
                  handleEdit(project);
                }}
              >
                Edit project
              </Button>
            </>
          ) : null}
        </Space>
      ),
      content: (
        <div style={{ marginTop: '20px' }}>
          <Tabs defaultActiveKey="overview">
            <Tabs.TabPane tab="Overview" key="overview">
              {project.health === 'At Risk' && (
                <Alert
                  message="Project At Risk"
                  description="Cost is approaching or exceeding budget. Review required."
                  type="warning"
                  showIcon
                  style={{ marginBottom: '16px' }}
                />
              )}
              {project.health === 'Critical' && (
                <Alert
                  message="Project Critical"
                  description="Project is significantly over budget or behind schedule. Immediate action required."
                  type="error"
                  showIcon
                  style={{ marginBottom: '16px' }}
                />
              )}
              
              <Row gutter={[16, 16]}>
                <Col span={12}>
                  <Text strong>Project Code:</Text>
                  <br />
                  <Text>{project.projectCode}</Text>
                </Col>
                <Col span={12}>
                  <Text strong>Status:</Text>
                  <br />
                  <Tag color={statusColors[project.status]}>{project.status}</Tag>
                  <Tag color={healthColors[project.health]} icon={healthIcons[project.health]}>
                    {project.health}
                  </Tag>
                </Col>
                <Col span={12}>
                  <Text strong>Customer:</Text>
                  <br />
                  <Text>{project.customer}</Text>
                </Col>
                <Col span={12}>
                  <Text strong>Project Manager:</Text>
                  <br />
                  <Text>
                    {userNameById.get(project.projectManager) ||
                      (project.projectManager === 'N/A' ? '—' : project.projectManager)}
                  </Text>
                </Col>
                <Col span={12}>
                  <Text strong>Start Date:</Text>
                  <br />
                  <Text>{fmtDate(project.startDate)}</Text>
                </Col>
                <Col span={12}>
                  <Text strong>End Date:</Text>
                  <br />
                  <Text>{fmtDate(project.endDate)}</Text>
                </Col>
                <Col span={12}>
                  <Text strong>Team Size:</Text>
                  <br />
                  <Text>{project.teamSize} members</Text>
                </Col>
                <Col span={12}>
                  <Text strong>Hours Logged:</Text>
                  <br />
                  <Text>{project.hoursLogged.toLocaleString()} hours</Text>
                </Col>
                <Col span={24}>
                  <Text strong>Description:</Text>
                  <br />
                  <Text>{project.description || 'No description'}</Text>
                </Col>
              </Row>
            </Tabs.TabPane>
            
            <Tabs.TabPane tab={<span><SafetyOutlined /> Quality Plan</span>} key="quality">
              <Space direction="vertical" style={{ width: '100%' }}>
                <Button type="primary" icon={<PlusOutlined />} onClick={() => window.location.href = `/qms/quality-plans?project_id=${project.id}`}>
                  Create Quality Plan
                </Button>
                <Text type="secondary">Quality plans ensure compliance with API Q1/Q2 requirements for this project.</Text>
                <div style={{ marginTop: '16px' }}>
                  <Text strong>Status: </Text>
                  <Tag color="default">No Quality Plan</Tag>
                </div>
                <div style={{ marginTop: '8px' }}>
                  <Text strong>API Compliance: </Text>
                  <Tag color="default">Not Applicable</Tag>
                </div>
              </Space>
            </Tabs.TabPane>
            
            <Tabs.TabPane tab={<span><WarningOutlined /> Risk Assessment</span>} key="risk">
              <Space direction="vertical" style={{ width: '100%' }}>
                <Button type="primary" icon={<PlusOutlined />} onClick={() => window.location.href = `/grc/risk-assessments?project_id=${project.id}`}>
                  Create Risk Assessment
                </Button>
                <Text type="secondary">Risk assessments identify and mitigate potential project risks.</Text>
                <div style={{ marginTop: '16px' }}>
                  <Text strong>Status: </Text>
                  <Tag color="default">No Risk Assessment</Tag>
                </div>
                <div style={{ marginTop: '8px' }}>
                  <Text strong>Overall Risk Level: </Text>
                  <Tag color="default">Not Assessed</Tag>
                </div>
              </Space>
            </Tabs.TabPane>
            
            <Tabs.TabPane tab="Financial" key="financial">
              <Row gutter={[16, 16]}>
                <Col span={8}>
                  <Statistic
                    title="Contract (linked SOs)"
                    value={project.budgetAmount}
                    prefix={<AedDirhamSign />}
                    valueStyle={{ color: '#145A54' }}
                  />
                </Col>
                <Col span={8}>
                  <Statistic
                    title="Estimation budget"
                    value={project.budgetedTotal}
                    prefix={<AedDirhamSign />}
                    valueStyle={{
                      color: project.budgetedTotal > project.budgetAmount ? '#f5222d' : undefined,
                    }}
                  />
                </Col>
                <Col span={8}>
                  <Statistic
                    title="Forecast P/L"
                    value={project.forecastProfit}
                    prefix={<AedDirhamSign />}
                    valueStyle={{
                      color: project.forecastProfit >= 0 ? '#52c41a' : '#f5222d',
                    }}
                  />
                </Col>
                <Col span={8}>
                  <Statistic
                    title="Forecast margin"
                    value={project.forecastMarginPct}
                    suffix="%"
                  />
                </Col>
                {project.invoicedTotal > 0 ? (
                  <Col span={8}>
                    <Statistic
                      title="Billed P/L"
                      value={project.billedProfit}
                      prefix={<AedDirhamSign />}
                      valueStyle={{
                        color: project.billedProfit >= 0 ? '#52c41a' : '#f5222d',
                      }}
                    />
                  </Col>
                ) : null}
                <Col span={8}>
                  <Button
                    type="link"
                    size="small"
                    icon={<LineChartOutlined />}
                    onClick={() => {
                      Modal.destroyAll();
                      navigate(`/prj/projects/${project.id}/status-check`);
                    }}
                    style={{ padding: 0 }}
                  >
                    Open health check for billed/cash P/L
                  </Button>
                </Col>
              </Row>
              
              <div style={{ marginTop: '24px' }}>
                <Text strong style={{ fontSize: '14px' }}>Cost Breakdown:</Text>
                <Table
                  dataSource={[
                    { key: '1', category: 'Material', amount: project.costBreakdown.material },
                    { key: '2', category: 'Labor', amount: project.costBreakdown.labor },
                    { key: '3', category: 'Subcontract', amount: project.costBreakdown.subcontract },
                    { key: '4', category: 'Equipment', amount: project.costBreakdown.equipment },
                    { key: '5', category: 'Overhead', amount: project.costBreakdown.overhead },
                    { key: 'total', category: 'Total', amount: costTotal },
                  ]}
                  columns={[
                    { title: 'Category', dataIndex: 'category', key: 'category' },
                    { 
                      title: 'Amount', 
                      dataIndex: 'amount', 
                      key: 'amount',
                      align: 'right',
                      render: (amount, record) => (
                        <Text strong={record.key === 'total'}>
                          {formatCurrency(amount)}
                        </Text>
                      ),
                    },
                    {
                      title: '% of Total',
                      key: 'percent',
                      align: 'right',
                      render: (_, record) => {
                        if (record.key === 'total') return '-';
                        const percent = costTotal > 0 ? ((record.amount / costTotal) * 100) : 0;
                        return `${percent.toFixed(1)}%`;
                      },
                    },
                  ]}
                  pagination={false}
                  size="small"
                  style={{ marginTop: '12px' }}
                />
              </div>
            </Tabs.TabPane>
          </Tabs>
        </div>
      ),
    });
  };

  const handleEdit = (project: Project) => {
    navigate(`/prj/projects/${project.id}/edit`);
  };

  const handleAdd = () => {
    navigate('/prj/projects/new');
  };

  // Calculate stats
  const totalProjects = projects.length;
  const activeProjects = projects.filter((p) => p.status === 'In Progress').length;
  const totalBudget = projects
    .filter((p) => p.status !== 'Cancelled')
    .reduce((sum, p) => sum + p.budgetAmount, 0);
  const totalBudgeted = projects
    .filter((p) => p.status !== 'Cancelled')
    .reduce((sum, p) => sum + p.budgetedTotal, 0);
  const atRiskProjects = projects.filter((p) => 
    ['At Risk', 'Critical'].includes(p.health) && p.status === 'In Progress'
  ).length;

  return (
    <div>
      {/* Page Header */}
      <div style={{ marginBottom: '24px' }}>
        <Title level={2}>Projects & Job Costing</Title>
        <Text type="secondary">
          Manage projects, track costs, and monitor profitability in real-time
        </Text>
      </div>

      {/* Summary Cards - Enhanced with NobleX */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} lg={6}>
          <NobleCard variant="kpi">
            <Statistic
              title="Total Projects"
              value={totalProjects}
              prefix={<ProjectOutlined />}
              valueStyle={{ color: '#145A54' }}
            />
          </NobleCard>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <NobleCard variant="kpi">
            <Statistic
              title="Active Projects"
              value={activeProjects}
              valueStyle={{ color: '#10B981' }}
            />
          </NobleCard>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          {atRiskProjects > 0 ? (
            <UrgentTaskCard
              title="At Risk Projects"
              summary={`${atRiskProjects} projects need attention`}
              timestamp="Review required"
            />
          ) : (
            <NobleCard variant="kpi">
              <Statistic
                title="At Risk"
                value={atRiskProjects}
                prefix={<CheckCircleOutlined />}
                valueStyle={{ color: '#10B981' }}
              />
            </NobleCard>
          )}
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <NobleCard variant="kpi">
            <Statistic
              title="Total Budget"
              value={totalBudget}
              prefix={<AedDirhamSign />}
              valueStyle={{ color: '#145A54' }}
            />
          </NobleCard>
        </Col>
      </Row>

      {/* Project Health Metrics - NobleX Progress Rings */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} lg={6}>
          <NobleCard variant="general">
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '16px 0' }}>
              <HealthScore value={100 - (atRiskProjects / Math.max(activeProjects, 1)) * 100} size="medium" />
              <Text strong style={{ marginTop: '12px', fontSize: '16px' }}>Portfolio Health</Text>
              <Text type="secondary" style={{ fontSize: '12px' }}>{atRiskProjects} At Risk</Text>
            </div>
          </NobleCard>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <NobleCard variant="general">
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '16px 0' }}>
              <CompletionRing 
                value={projects.reduce((sum, p) => sum + p.progressPercent, 0) / Math.max(projects.length, 1)} 
                size="medium" 
              />
              <Text strong style={{ marginTop: '12px', fontSize: '16px' }}>Avg Progress</Text>
              <Text type="secondary" style={{ fontSize: '12px' }}>{projects.length} Projects</Text>
            </div>
          </NobleCard>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <NobleCard variant="general">
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '16px 0' }}>
              <CompletionRing
                value={totalBudget > 0 ? Math.min((totalBudgeted / totalBudget) * 100, 100) : 0}
                size="medium"
              />
              <Text strong style={{ marginTop: '12px', fontSize: '16px' }}>Budget utilization</Text>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                {totalBudget > 0 ? ((totalBudgeted / totalBudget) * 100).toFixed(1) : '0.0'}%
              </Text>
            </div>
          </NobleCard>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <NobleCard variant="general">
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '16px 0' }}>
              <CompletionRing 
                value={(projects.filter(p => p.status === 'Completed').length / Math.max(totalProjects, 1)) * 100} 
                size="medium" 
              />
              <Text strong style={{ marginTop: '12px', fontSize: '16px' }}>Completion Rate</Text>
              <Text type="secondary" style={{ fontSize: '12px' }}>{projects.filter(p => p.status === 'Completed').length} Completed</Text>
            </div>
          </NobleCard>
        </Col>
      </Row>

      {/* Performance Alert */}
      {totalBudgeted > totalBudget && (
        <Alert
          message="Portfolio over contract"
          description={`Total budgeted cost (${formatCurrency(totalBudgeted)}) exceeds total contract value (${formatCurrency(totalBudget)}) by ${formatCurrency(totalBudgeted - totalBudget)}`}
          type="warning"
          showIcon
          closable
          style={{ marginBottom: '16px' }}
        />
      )}

      {/* Main Card - NobleX */}
      <NobleCard variant="general">
        {/* Toolbar */}
        <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
          <Space>
            <Search
              placeholder="Search number, name, customer…"
              allowClear
              style={{ width: 300 }}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
            <Select value={statusFilter} style={{ width: 170 }} onChange={setStatusFilter}>
              <Select.Option value="all">All status</Select.Option>
              <Select.Option value="Planning">Planning</Select.Option>
              <Select.Option value="In Progress">In Progress</Select.Option>
              <Select.Option value="On Hold">On Hold</Select.Option>
              <Select.Option value="Completed">Completed</Select.Option>
              <Select.Option value="Cancelled">Cancelled</Select.Option>
            </Select>
            <Select value={healthFilter} style={{ width: 150 }} onChange={setHealthFilter}>
              <Select.Option value="all-health">All health</Select.Option>
              <Select.Option value="Healthy">Healthy</Select.Option>
              <Select.Option value="At Risk">At Risk</Select.Option>
              <Select.Option value="Critical">Critical</Select.Option>
            </Select>
          </Space>
          <NobleButton variant="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            Create Project
          </NobleButton>
        </div>

        {/* Error Display */}
        {error && (
          <Alert
            message="Error Loading Projects"
            description={error instanceof Error ? error.message : 'Failed to load projects. Please check the console for details.'}
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        {/* Empty State */}
        {!isLoading && !error && filteredProjects.length === 0 && projects.length > 0 && (
          <Alert
            message="No Projects Match Search"
            description={`No projects match "${searchText}". Try a different search term.`}
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}
        
        {!isLoading && !error && projects.length === 0 && (
          <Alert
            message="No Projects Found"
            description="There are no projects in the system. Create your first project to get started."
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        {/* Table */}
        <Table
          columns={columns}
          dataSource={filteredProjects}
          loading={isLoading}
          rowKey="id"
          pagination={{
            defaultPageSize: 20,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} projects`,
          }}
          size="small"
          scroll={{ x: 1600 }}
        />
      </NobleCard>

      <Modal
        title={renameTarget ? `Rename — ${renameTarget.projectCode}` : 'Rename project'}
        open={renameOpen}
        onCancel={() => {
          setRenameOpen(false);
          setRenameTarget(null);
        }}
        onOk={submitRename}
        okText="Save"
        confirmLoading={renameSubmitting}
        destroyOnClose
      >
        <Form form={renameForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="project_name"
            label="Project name"
            rules={[{ required: true, message: 'Please enter a project name' }]}
          >
            <Input placeholder="Project name" maxLength={255} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

